/**
 * MaintenancesTableRow - Linha da tabela de manutenções
 * Memoizado para evitar re-renders desnecessários
 */

import React, { memo } from 'react';
import {
  TableRow,
  TableCell,
  Typography,
  Avatar,
  Chip,
  Checkbox,
} from '@mui/material';
import BuildIcon from '@mui/icons-material/Build';
import dayjs from 'dayjs';

import { usePremiumTokens } from '../../common/theme/usePremiumTokens';
import { useTranslation } from '../../common/components/LocalizationProvider';
import usePositionAttributes from '../../common/attributes/usePositionAttributes';
import { formatDistance, formatSpeed, formatTime } from '../../common/util/formatter';
import { useAttributePreference } from '../../common/util/preferences';
import CollectionActions from './CollectionActions';

// Helper para cores do status - USA TOKENS DINÂMICOS
const getStatusColors = (status, tokens) => {
  const colors = {
    active: {
      text: tokens?.semantic?.success,
      bg: tokens?.semantic?.successBg,
      border: `${tokens?.semantic?.success}40`,
    },
    disabled: {
      text: tokens?.semantic?.error,
      bg: tokens?.semantic?.errorBg,
      border: `${tokens?.semantic?.error}40`,
    },
  };

  return colors[status] || colors.active;
};

const MaintenancesTableRow = memo(({
  row,
  onRefresh,
  selected = false,
  onSelect,
}) => {
  const t = useTranslation();
  const tokens = usePremiumTokens();
  const positionAttributes = usePositionAttributes(t);
  const speedUnit = useAttributePreference('speedUnit');
  const distanceUnit = useAttributePreference('distanceUnit');

  const status = row.disabled ? 'disabled' : 'active';
  const statusColors = getStatusColors(status, tokens);

  // Cor do avatar baseada no status
  const avatarColor = statusColors.text;

  // Helper de conversão
  const convertAttribute = (key, start, value) => {
    const attribute = positionAttributes[key];
    if (key.endsWith('Time')) {
      if (start) return formatTime(dayjs(value).toISOString(), 'date');
      return `${value / 86400000} ${t('sharedDays')}`;
    }
    if (attribute && attribute.dataType) {
      switch (attribute.dataType) {
        case 'speed':
          return formatSpeed(value, speedUnit, t);
        case 'distance':
          return formatDistance(value, distanceUnit, t);
        case 'hours':
          return `${value / 3600000} ${t('sharedHours')}`;
        default:
          return value;
      }
    }
    return value;
  };

  return (
    <TableRow
      hover
      selected={selected}
      sx={{
        cursor: 'default',
        transition: tokens?.transitions?.colors || 'all 0.15s ease',
        '&:hover': {
          backgroundColor: tokens?.surface?.hover || 'rgba(255,255,255,0.03)',
        },
        '&.Mui-selected': {
          backgroundColor: `${tokens?.accent?.primary}10`,
          '&:hover': {
            backgroundColor: `${tokens?.accent?.primary}15`,
          },
        },
      }}
    >
      {/* Checkbox */}
      {onSelect && (
        <TableCell padding="checkbox" sx={{ py: 1, width: 48 }}>
          <Checkbox
            checked={selected}
            onChange={() => onSelect(row.id)}
            sx={{
              color: tokens?.text?.tertiary,
              '&.Mui-checked': {
                color: tokens?.accent?.primary,
              },
            }}
          />
        </TableCell>
      )}

      {/* Avatar */}
      <TableCell sx={{ py: 1, width: 48 }}>
        <Avatar
          sx={{
            width: 32,
            height: 32,
            bgcolor: `${avatarColor}20`,
            color: avatarColor,
          }}
        >
          <BuildIcon fontSize="small" />
        </Avatar>
      </TableCell>

      {/* ID */}
      <TableCell sx={{ py: 1, width: 60 }}>
        <Typography variant="body2" sx={{ color: tokens?.text?.muted, fontFamily: 'monospace', fontSize: '0.8rem' }}>
          {row.id}
        </Typography>
      </TableCell>

      {/* Name */}
      <TableCell sx={{ py: 1 }}>
        <Typography variant="body2" fontWeight={500} noWrap sx={{ maxWidth: 200 }}>
          {row.name}
        </Typography>
      </TableCell>

      {/* Type */}
      <TableCell sx={{ py: 1 }}>
        <Chip
          label={positionAttributes[row.type]?.name || row.type || '-'}
          size="small"
          sx={{
            height: 22,
            fontSize: '0.7rem',
            backgroundColor: tokens?.semantic?.infoBg,
            color: tokens?.semantic?.info,
            border: `1px solid ${tokens?.semantic?.info}40`,
          }}
        />
      </TableCell>

      {/* Start */}
      <TableCell sx={{ py: 1 }}>
        <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem' }}>
          {row.type && row.start !== undefined
            ? convertAttribute(row.type, true, row.start)
            : '-'}
        </Typography>
      </TableCell>

      {/* Period */}
      <TableCell sx={{ py: 1 }}>
        <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem' }}>
          {row.type && row.period !== undefined
            ? convertAttribute(row.type, false, row.period)
            : '-'}
        </Typography>
      </TableCell>

      {/* Status */}
      <TableCell align="center" sx={{ py: 1 }}>
        <Chip
          label={row.disabled ? t('sharedDisabled') : t('deviceStatusActive')}
          size="small"
          className={row.disabled ? 'status-disabled' : 'status-active'}
          sx={{
            minWidth: 70,
            height: 22,
            fontSize: '0.7rem',
            fontWeight: 600,
            backgroundColor: statusColors.bg,
            color: statusColors.text,
            border: `1px solid ${statusColors.border}`,
            '& .MuiChip-label': { px: 1 },
          }}
        />
      </TableCell>

      {/* Actions */}
      <TableCell align="right" sx={{ py: 1 }}>
        <CollectionActions
          itemId={row.id}
          editPath="/settings/maintenance"
          endpoint="maintenance"
          setTimestamp={onRefresh}
        />
      </TableCell>
    </TableRow>
  );
});

MaintenancesTableRow.displayName = 'MaintenancesTableRow';

export default MaintenancesTableRow;
